import { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography
} from "@mui/material";
import { Check, Close, Edit } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const normalizeAliasList = (aliases: string[]): string[] => {
  const seen = new Set<string>();
  return aliases
    .map(alias => alias.trim())
    .filter(Boolean)
    .filter(alias => {
      const key = alias.toLowerCase();
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
};

/** Scroll the chip + input area when many aliases wrap (px). */
const ALIASES_FIELD_MAX_HEIGHT_PX = 300;

interface Props {
  aliases: string[];
  onAliasesChange: (aliases: string[]) => void;
}

export default function DepartmentAliasesField({ aliases, onAliasesChange }: Readonly<Props>) {
  const { t } = useTranslation();
  const [editingAliasIndex, setEditingAliasIndex] = useState<number | null>(null);
  const [editingAliasValue, setEditingAliasValue] = useState("");

  const cancelEditing = () => {
    setEditingAliasIndex(null);
    setEditingAliasValue("");
  };

  useEffect(() => {
    if (editingAliasIndex === null) {
      return;
    }
    if (editingAliasIndex >= aliases.length) {
      setEditingAliasIndex(null);
      setEditingAliasValue("");
    }
  }, [aliases, editingAliasIndex]);

  const saveEditedAlias = (index: number) => {
    const updatedAliases = [...aliases];
    const trimmedAlias = editingAliasValue.trim();
    if (trimmedAlias) {
      updatedAliases[index] = trimmedAlias;
    } else {
      updatedAliases.splice(index, 1);
    }
    onAliasesChange(normalizeAliasList(updatedAliases));
    cancelEditing();
  };

  const isEditDialogOpen = editingAliasIndex !== null;

  return (
    <>
      <Box
        sx={{
          width: "100%",
          minWidth: 0,
          maxHeight: ALIASES_FIELD_MAX_HEIGHT_PX,
          overflowY: "auto",
          overflowX: "hidden",
          pr: 0.25
        }}
      >
        <Autocomplete
          multiple
          freeSolo
          data-testid={"aliases-field"}
          id="aliases"
          options={[]}
          value={aliases}
          sx={{
            width: "100%",
            minWidth: 0,
            "& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot": {
              flexWrap: "wrap",
              alignItems: "flex-start",
              rowGap: 0.5,
              minWidth: 0
            },
            /**
             * Typing row: MUI uses width:0 + minWidth:30px so the input can shrink to nothing next to wide chips.
             * Use flex-basis 100% so the input moves to its own row, width:0 + flexGrow:1 + minWidth:0 so it
             * stays inside the padded outline (avoids placeholder overflowing horizontally).
             */
            "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
              flexBasis: "100%",
              flexGrow: 1,
              flexShrink: 1,
              width: "0 !important",
              minWidth: "0 !important",
              maxWidth: "100%",
              boxSizing: "border-box",
              overflow: "hidden",
              textOverflow: "ellipsis"
            },
            "& .MuiOutlinedInput-root.MuiAutocomplete-inputRoot:has(.MuiAutocomplete-tag) .MuiAutocomplete-input":
              {
                marginTop: 0.5
              }
          }}
          onChange={(event, value) => {
            event.persist();
            onAliasesChange(normalizeAliasList(value));
          }}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => {
              const { key, ...chipProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  {...chipProps}
                  title={option}
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        minWidth: 0,
                        maxWidth: "100%"
                      }}
                    >
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          minWidth: 0
                        }}
                      >
                        {option}
                      </Typography>
                      <Edit
                        fontSize="small"
                        data-testid={`alias-edit-trigger-${index}`}
                        aria-label={t("Edit")}
                        sx={{ flexShrink: 0, cursor: "pointer" }}
                        onClick={event => {
                          event.stopPropagation();
                          setEditingAliasIndex(index);
                          setEditingAliasValue(option);
                        }}
                      />
                    </Box>
                  }
                  deleteIcon={<Close fontSize="small" />}
                  sx={{
                    mr: 1,
                    mb: 1,
                    maxWidth: "100%",
                    "& .MuiChip-label": {
                      display: "flex",
                      minWidth: 0,
                      maxWidth: "100%",
                      overflow: "hidden"
                    }
                  }}
                />
              );
            })
          }
          renderInput={params => (
            <TextField
              {...params}
              variant="outlined"
              fullWidth
              placeholder={t("Add alias by typing")}
            />
          )}
        />
      </Box>

      {/* edit alias dialog */}
      <Dialog open={isEditDialogOpen} onClose={cancelEditing} maxWidth="xs" fullWidth>
        <DialogTitle>{t("Edit alias")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            value={editingAliasValue}
            onChange={e => setEditingAliasValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Escape") {
                cancelEditing();
                return;
              }
              if (e.key === "Enter") {
                e.preventDefault();
                if (editingAliasIndex !== null) {
                  saveEditedAlias(editingAliasIndex);
                }
              }
            }}
            placeholder={t("Edit alias")}
            slotProps={{
              input: {
                endAdornment: (
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <IconButton
                      size="small"
                      data-testid="edit-alias-dialog-confirm-btn"
                      aria-label={t("Confirm")}
                      onMouseDown={event => event.preventDefault()}
                      onClick={() => {
                        if (editingAliasIndex !== null) {
                          saveEditedAlias(editingAliasIndex);
                        }
                      }}
                    >
                      <Check fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      data-testid="edit-alias-dialog-cancel-btn"
                      aria-label={t("Cancel")}
                      onMouseDown={event => event.preventDefault()}
                      onClick={cancelEditing}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                )
              }
            }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
